import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';

import { appBaseUrl } from '@core/http/api-url.util';

import {
  DEFAULT_DOCUMENT_TITLE,
  DEFAULT_GOOGLEBOT,
  DEFAULT_KEYWORDS,
  DEFAULT_META_DESCRIPTION,
  DEFAULT_OG_DESCRIPTION,
  DEFAULT_OG_IMAGE_ALT,
  DEFAULT_OG_IMAGE_HEIGHT,
  DEFAULT_OG_IMAGE_PATH,
  DEFAULT_OG_IMAGE_WIDTH,
  DEFAULT_TWITTER_DESCRIPTION,
  SITE_NAME,
  TWITTER_CARD,
  TWITTER_CREATOR,
  TWITTER_SITE,
} from './seo.constants';
import { buildLandingStructuredData } from './home-structured-data';
import {
  buildAboutStructuredData,
  buildContactStructuredData,
} from './organization-structured-data';
import type { SeoRouteData } from './seo.types';

const JSON_LD_ATTR = 'data-akalan-jsonld';
const ARTICLE_JSON_LD_ATTR = 'data-akalan-article-jsonld';
const RSS_LINK_ATTR = 'data-akalan-rss';

@Injectable({ providedIn: 'root' })
export class SeoService {
  private readonly title = inject(Title);
  private readonly meta = inject(Meta);
  private readonly document = inject(DOCUMENT);

  /**
   * Applies meta tags from merged route `data.seo` + sensible defaults.
   * @param routerUrl path + query from `Router.url` (canonical strips query)
   */
  applyFromRouteData(data: SeoRouteData, routerUrl: string): void {
    const base = appBaseUrl();
    const canonical = this.canonicalUrl(base, routerUrl, data.canonicalPath);

    const pageTitle = this.formatTitle(data.title);
    this.title.setTitle(pageTitle);

    const description = (data.description ?? '').trim() || DEFAULT_META_DESCRIPTION;
    this.meta.updateTag({ name: 'description', content: description });

    const ogDescription = (data.ogDescription ?? '').trim() || description;
    const twitterDescription = (data.twitterDescription ?? '').trim() || ogDescription;

    const keywords = (data.keywords ?? '').trim() || DEFAULT_KEYWORDS;
    this.meta.updateTag({ name: 'keywords', content: keywords });

    const robots = (data.robots ?? '').trim() || 'index, follow';
    this.meta.updateTag({ name: 'robots', content: robots });
    this.meta.updateTag({
      name: 'googlebot',
      content: /noindex/i.test(robots) ? robots : DEFAULT_GOOGLEBOT,
    });

    this.meta.updateTag({ name: 'author', content: SITE_NAME });
    this.meta.updateTag({ name: 'publisher', content: SITE_NAME });
    this.meta.updateTag({ name: 'creator', content: SITE_NAME });
    this.meta.updateTag({ name: 'category', content: 'Education and Career' });

    const ogTitle = (data.ogTitle ?? '').trim() || pageTitle;
    this.meta.updateTag({ property: 'og:site_name', content: SITE_NAME });
    this.meta.updateTag({ property: 'og:title', content: ogTitle });
    this.meta.updateTag({ property: 'og:description', content: ogDescription });
    this.meta.updateTag({ property: 'og:url', content: canonical });
    this.meta.updateTag({ property: 'og:type', content: data.ogType ?? 'website' });
    this.meta.updateTag({ property: 'og:locale', content: 'en_IN' });

    this.clearArticleMetaTags();
    if (data.ogType === 'article') {
      if (data.articlePublishedTime) {
        this.meta.updateTag({
          property: 'article:published_time',
          content: data.articlePublishedTime,
        });
      }
      if (data.articleModifiedTime) {
        this.meta.updateTag({
          property: 'article:modified_time',
          content: data.articleModifiedTime,
        });
      }
      if (data.articleAuthor) {
        this.meta.updateTag({ property: 'article:author', content: data.articleAuthor });
      }
    }

    const imageUrl = this.resolveOgImage(data.image, base);
    this.meta.updateTag({ property: 'og:image', content: imageUrl });
    const useDefaultDims = !(data.image ?? '').trim();
    if (useDefaultDims) {
      this.meta.updateTag({ property: 'og:image:width', content: DEFAULT_OG_IMAGE_WIDTH });
      this.meta.updateTag({ property: 'og:image:height', content: DEFAULT_OG_IMAGE_HEIGHT });
      this.meta.updateTag({ property: 'og:image:alt', content: DEFAULT_OG_IMAGE_ALT });
    } else {
      this.meta.removeTag("property='og:image:width'");
      this.meta.removeTag("property='og:image:height'");
      this.meta.removeTag("property='og:image:alt'");
    }

    this.meta.updateTag({ name: 'twitter:card', content: TWITTER_CARD });
    this.meta.updateTag({ name: 'twitter:site', content: TWITTER_SITE });
    this.meta.updateTag({ name: 'twitter:creator', content: TWITTER_CREATOR });
    this.meta.updateTag({ name: 'twitter:title', content: ogTitle });
    this.meta.updateTag({ name: 'twitter:description', content: twitterDescription });
    this.meta.updateTag({ name: 'twitter:image', content: imageUrl });

    this.setCanonicalHref(canonical);
    this.applyStructuredData(data.structuredData);
  }

  /**
   * After async content loads (e.g. report fetch): refresh title, meta description, and OG/Twitter copy.
   * Pass `title` that already includes “Akalan” when you want the exact browser tab string.
   */
  /** Injects Article schema (blog posts). Cleared when navigating away or re-applying. */
  applyArticleJsonLd(graph: Record<string, unknown>): void {
    this.clearArticleJsonLdPrivate();
    const head = this.document.head;
    if (!head) return;

    const script = this.document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute(ARTICLE_JSON_LD_ATTR, '');
    script.textContent = JSON.stringify(graph);
    head.appendChild(script);
  }

  /** Adds RSS autodiscovery link for blog feed readers and crawlers. */
  setRssFeedAlternate(href: string, title = 'Akalan Blog'): void {
    this.clearRssFeedAlternate();
    const head = this.document.head;
    if (!head) return;

    const link = this.document.createElement('link');
    link.setAttribute('rel', 'alternate');
    link.setAttribute('type', 'application/rss+xml');
    link.setAttribute('title', title);
    link.setAttribute('href', href);
    link.setAttribute(RSS_LINK_ATTR, '');
    head.appendChild(link);
  }

  clearRssFeedAlternate(): void {
    const head = this.document.head;
    if (!head) return;
    head.querySelectorAll(`link[${RSS_LINK_ATTR}]`).forEach((node) => node.remove());
  }

  clearArticleJsonLd(): void {
    this.clearArticleJsonLdPrivate();
  }

  patchPageMeta(rawTitle: string, description: string): void {
    const pageTitle = this.formatTitle(rawTitle);
    this.title.setTitle(pageTitle);
    const d = description.trim();
    if (d) {
      this.meta.updateTag({ name: 'description', content: d });
      this.meta.updateTag({ property: 'og:description', content: d });
      this.meta.updateTag({ name: 'twitter:description', content: d });
    }
    this.meta.updateTag({ property: 'og:title', content: pageTitle });
    this.meta.updateTag({ name: 'twitter:title', content: pageTitle });
  }

  private canonicalUrl(base: string, routerUrl: string, overridePath?: string): string {
    const raw = (overridePath ?? routerUrl.split(/[?#]/)[0] ?? '/').trim() || '/';
    let path = raw.startsWith('/') ? raw : `/${raw}`;
    if (path.length > 1) path = path.replace(/\/+$/, '');
    return `${base}${path}`;
  }

  private formatTitle(raw: string | undefined): string {
    const t = (raw ?? '').trim();
    if (!t) return DEFAULT_DOCUMENT_TITLE;
    if (t.includes(SITE_NAME)) return t;
    return `${t} | ${SITE_NAME}`;
  }

  private resolveOgImage(image: string | undefined, baseUrl: string): string {
    const s = (image ?? '').trim();
    if (!s) return `${baseUrl}${DEFAULT_OG_IMAGE_PATH}`;
    if (s.startsWith('http://') || s.startsWith('https://')) return s;
    const path = s.startsWith('/') ? s : `/${s}`;
    return `${baseUrl}${path}`;
  }

  private setCanonicalHref(href: string): void {
    const head = this.document.head;
    if (!head) return;
    let link = head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = this.document.createElement('link');
      link.setAttribute('rel', 'canonical');
      head.appendChild(link);
    }
    link.setAttribute('href', href);
  }

  private applyStructuredData(kind: SeoRouteData['structuredData']): void {
    this.clearStructuredData();
    if (!kind) return;

    const graphs =
      kind === 'landing'
        ? [buildLandingStructuredData()]
        : kind === 'about'
          ? [buildAboutStructuredData()]
          : kind === 'contact'
            ? [buildContactStructuredData()]
            : [];
    const head = this.document.head;
    if (!head || graphs.length === 0) return;

    for (const graph of graphs) {
      const script = this.document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute(JSON_LD_ATTR, '');
      script.textContent = JSON.stringify(graph);
      head.appendChild(script);
    }
  }

  private clearStructuredData(): void {
    const head = this.document.head;
    if (!head) return;
    head.querySelectorAll(`script[${JSON_LD_ATTR}]`).forEach((node) => node.remove());
  }

  private clearArticleJsonLdPrivate(): void {
    const head = this.document.head;
    if (!head) return;
    head.querySelectorAll(`script[${ARTICLE_JSON_LD_ATTR}]`).forEach((node) => node.remove());
  }

  private clearArticleMetaTags(): void {
    for (const property of [
      'article:published_time',
      'article:modified_time',
      'article:author',
    ]) {
      this.meta.removeTag(`property='${property}'`);
    }
  }
}
